import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Accordion, Button, Checkbox, Glyphicon } from 'react-bootstrap';
import { Badge, Panel } from 'axway-react-ui';
import Component from './Component';
import Message from './Message';

const API_BUILDER_CORE = 'API Builder Core';

class ComponentPanel extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);
		this.state = {
			readReleaseNotes: false
		};
	}

	getReleaseName() {
		const { components } = this.props;
		const { releaseName } = components[0];
		return releaseName;
	}

	getComponentDocLink(component) {
		const url = component.releaseNotes || component.docs;
		if (!url) {
			return null;
		}
		let text;
		if (component.releaseNotes) {
			text = this.intl.formatMsg('release notes');
		} else {
			text = this.intl.formatMsg('documentation');
		}
		return (
			<a
				href={url}
				className="component-doc"
				target="_blank"
				// Using target="_blank" without
				// rel="noopener" is a security risk:
				// https://html.spec.whatwg.org/multipage/links.html#link-type-noopener
				rel="noopener noreferrer"
			>
				{text}
			</a>
		);
	}

	getUnmetPrerequisites() {
		const { hasUpdates } = this.props;
		const unmetPrerequisites = [];
		if (hasUpdates) {
			unmetPrerequisites.push(
				<Message
					key="there-are-updates"
					type="cross-circle"
					text={this.intl.formatMsg('You need to install all available updates before installing upgrades.')}
				/>
			);
		}
		const component = this.props.components[0];
		const { prerequisites } = component;
		if (!prerequisites) {
			return unmetPrerequisites;
		}
		for (const name in prerequisites) {
			const req = prerequisites[name];
			if (req.compatible) {
				continue;
			}
			if (name === 'installedPlugins') {
				for (const unmet of req.list) {
					const text = this.intl.formatMsg('This cannot be installed because {name} is incompatible with it.', 'prereq_incompat', unmet);
					unmetPrerequisites.push(
						<Message
							key={req.name}
							type="cross-circle"
							text={text}
						/>
					);
				}
				continue;
			}

			const text = this.intl.formatMsg('This cannot be installed because it requires {name} {requires} (current {current}).', 'prereq_unmet', req);
			unmetPrerequisites.push(
				<Message
					key={req.name}
					type="cross-circle"
					text={text}
				/>
			);
		}
		return unmetPrerequisites;
	}

	renderComponents(unmetPrerequisites) {
		const { components, type } = this.props;
		const isUpdate = type === 'updates';
		const showName = isUpdate;
		const showLink = isUpdate || unmetPrerequisites.length;

		return components.map(component => {
			const link = showLink ? this.getComponentDocLink(component) : null;
			let releaseName;
			if (!isUpdate && component.name === API_BUILDER_CORE) {
				// only show release name in upgrades of core component
				releaseName = this.getReleaseName();
			}
			return (
				<Component
					type={type}
					key={component.name}
					name={component.name}
					showName={showName}
					releaseName={releaseName}
					fromVersion={component.fromVersion}
					toVersion={component.toVersion}
					components={component.components}
					link={link}
				/>
			);
		});
	}

	renderTitle(unmetPrerequisites) {
		const {
			type,
			onInstall,
			components,
			productName
		} = this.props;

		const componentTitle = [];
		let badgeSeverity;
		let installButtonText;

		if (type === 'updates') {
			badgeSeverity = 'info';
			installButtonText = this.intl.formatMsg('Update');
			const releaseName = this.getReleaseName();
			if (releaseName) {
				componentTitle.push(
					<span key="title" className="title">
						{this.intl.formatMsg('Update to {productName} "{releaseName}"', { productName, releaseName })}
					</span>);
			} else {
				componentTitle.push(
					<span key="title" className="title">
						{this.intl.formatMsg('Updates are available')}
					</span>
				);
			}
		} else {
			badgeSeverity = 'warning';
			installButtonText = this.intl.formatMsg('Upgrade');
			const component = components[0];
			const { name } = components[0];
			if (name === API_BUILDER_CORE) {
				// "API Builder v5"
				const majorVersion = parseInt(component.toVersion, 10);
				componentTitle.push(
					<span key="title" className="title">
						{productName} v{majorVersion}
					</span>);
			} else {
				componentTitle.push(
					<span key="title" className="title">
						{name}
					</span>);
			}
		}

		const badgeCount = components.length;
		if (badgeCount > 1) {
			componentTitle.push(' ');
			componentTitle.push(
				<Badge key="badge" bsStyle={badgeSeverity}>{badgeCount}</Badge>
			);
		}

		// this logic is a bit complicated. this is for "upgrades" only. when
		// there are no unmet prereqs, and the component has some kind of
		// release-note/document, then the user must tick the "I read the notes"
		// box. if there are no unmet prereqs, and there is no doc, then they
		// can simply install it.
		const unmetReleaseNotes = !unmetPrerequisites.length
			&& !!(components[0].releaseNotes || components[0].docs)
			&& !this.state.readReleaseNotes;

		// update actions can never be disabled. disable the upgrade action if
		// there are any unmet prereqs, or if they didn't tick "read docs".
		const actionDisabled = type === 'upgrades'
			&& (!!unmetPrerequisites.length || unmetReleaseNotes);

		return (
			[
				<span
					key={`${componentTitle}_title`}
				>
					{componentTitle}
				</span>,
				<Button
					key={`${componentTitle}_button`}
					className="update"
					bsStyle="primary"
					bsSize="large"
					onClick={onInstall}
					disabled={actionDisabled}
				>
					<Glyphicon glyph=" gg icon-GG-arrow-up" />
					{installButtonText}
				</Button>
			]
		);
	}

	renderPrerequisites(unmetPrerequisites) {
		if (unmetPrerequisites.length < 2) {
			return null;
		}
		return (
			<div>
				<br />
				<b>
					{this.intl.formatMsg('Prerequisites')}
				</b>
				<ul className="prerequesites">
					{unmetPrerequisites.map((a, i) => <li key={i}>{a}</li>)}
				</ul>
			</div>
		);
	}

	renderFooter(unmetPrerequisites) {
		const { type } = this.props;
		if (type === 'updates') {
			return this.intl.formatMsg('Additional updates may be available from npm that are not in this list and they may contain important patches and security fixes.');
		}
		// check prerequisites
		if (unmetPrerequisites.length === 1) {
			return unmetPrerequisites[0];
		} else if (unmetPrerequisites.length > 1) {
			return (
				<Message
					key={unmetPrerequisites[0]}
					type="cross-circle"
					text={this.intl.formatMsg('This cannot be installed because there are unmet prerequisites.')}
				/>
			);
		} else {
			const component = this.props.components[0];
			const link = this.getComponentDocLink(component);
			if (link) {
				return (
					<Checkbox onChange={(ev) => {
						this.setState({
							readReleaseNotes: ev.target.checked
						});
					}}
					>
						{this.intl.formatHTMLMsg('I have read the {link} and understand the breaking changes.', { link })}
					</Checkbox>
				);
			} else {
				return (
					<Message
						key="upgrade-okay"
						type="info"
						text={this.intl.formatMsg('You can install this upgrade.')}
					/>
				);
			}
		}
	}

	render() {
		const { title } = this.props;
		const unmetPrerequisites = this.getUnmetPrerequisites();
		const footer = this.renderFooter(unmetPrerequisites);
		const header = this.renderTitle(unmetPrerequisites);

		return (
			<Accordion id={title}>
				<Panel
					hasIcon
					header={header}
					footer={footer}
					independent
				>
					<div className="updates-list-item">
						<ul>
							{this.renderComponents(unmetPrerequisites)}
						</ul>
						{this.renderPrerequisites(unmetPrerequisites)}
					</div>
				</Panel>
			</Accordion>
		);
	}
}

ComponentPanel.propTypes = {
	/**
	 * The title of the component (must be unique).
	 */
	title: PropTypes.string.isRequired,
	/**
	 * The type of component panel to render.
	 */
	type: PropTypes.oneOf([ 'updates', 'upgrades' ]).isRequired,
	/**
	 * The components.
	 */
	components: PropTypes.array.isRequired,
	/**
	 * The function to use to refresh the UI
	 */
	onInstall: PropTypes.func.isRequired,
	/**
	 * The product name.
	 */
	productName: PropTypes.string.isRequired,
	/**
	 * When `type` is `upgrades`, this will have an implicit prerequisite on
	 * uninstalled updates.
	 */
	hasUpdates: PropTypes.bool
};

export default injectIntl(ComponentPanel);
